import Vue from 'vue';
import Vuex from 'vuex';
import { makeBasicAuthToken } from '@/services/auth';
import axios from '@/AxiosRequests';
import getEnv from '@/services/env';
import storageManager from '@/services/storageManager';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    status: storageManager.getItem('token') ? 'success' : '',
    token: storageManager.getItem('token') || '',
    authenticated: false,
    adminPassword: '',
    adminSettings: {},
    loadingParams: {},
    totalCount: 0,
    userRights: [],
    username: '',
    timeout: 10,
    user: '',
    deftimeout: parseInt(getEnv('DEFAULT_TIMEOUT'), 10),
    isDebug: false,
    heightOffset: getEnv('HEIGHT_OFFSET'),
    bankIcon: getEnv('BANK_ICON'),
    services: Object.keys(getEnv('SERVICES')),
  },
  actions: {
    getUserData({ commit }, { vm }) {
      return new Promise((resolve, reject) => {
        vm.$axios.login
          .getUserData()
          .then((response) => {
            const { rights, name, timeout } = response.data;
            const usData = response.data;
            commit('auth_success', { rights, name, timeout, usData });
            resolve(response);
          })
          .catch((err) => {
            commit('auth_error');
            commit('remove_token');
            storageManager.removeItem('token');
            reject(err);
          });
      });
    },
    login({ commit }, { data, vm }) {
      return new Promise((resolve, reject) => {
        commit('auth_request');

        const { login, password } = data;
        const token = makeBasicAuthToken(login, password);

        commit('set_token', token);

        vm.$axios.login
          .getIdentifier()
          .then((response) => {
            const { data } = response;
            const { identifier } = data;

            commit('set_token', identifier);
            storageManager.setItem('token', identifier);

            return response;
          })
          .then(async () => {
            await vm.$axios.login.getUserData().then((response) => {
              const { rights, name, timeout } = response.data;
              const usData = response.data;
              commit('auth_success', { rights, name, timeout, usData });
              commit('setUsername', login);
              resolve(response);
            });
          })
          .catch((err) => {
            commit('auth_error');
            commit('remove_token');
            storageManager.removeItem('token');
            reject(err);
          });
      });
    },
    change_password({ commit }, { data, vm }) {
      return new Promise((resolve, reject) => {
        const { previous_password, new_password, forced } = data;

        if (forced) {
          vm.$axios.login
            .reset({ newPassword: new_password })
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              commit('remove_token');
              reject(error);
            });
        } else {
          const passData = {
            OldPassword: previous_password,
            NewPassword: new_password,
          };
          vm.$axios.login
            .changePassword(passData)
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              reject(error);
            });
        }
      });
    },
    relogin({ commit }) {
      return new Promise((resolve, reject) => {
        commit('auth_request');

        axios.login
          .reLogin()
          .then((response) => {
            const { data } = response;
            const { rights, name, timeout } = data;

            commit('reauth_success', { rights, name, timeout, data });
            resolve(response);
          })
          .catch((err) => {
            commit('auth_error');
            storageManager.removeItem('token');
            reject(err);
          });
      });
    },
    logout({ commit }, { vm }) {
      return new Promise((resolve) => {
        vm.$axios.login
          .logOut()
          .then(() => {
            commit('logout');
            storageManager.removeItem('token');
            resolve();
          })
          .catch(() => {
            commit('logout');
            storageManager.removeItem('token');
            resolve();
          });
      });
    },
  },
  getters: {
    BANK_TITLE: (state) => state.loadingParams.bank_Title,
    BANK_ICON: (state) => state.loadingParams.bank_Icon,
    isLoggedIn: (state) => !!state.token && state.status === 'success',
    authStatus: (state) => state.status,
    HAS_REPORTS: (state) => state.loadingParams.enable_Reports,
    DISPLAY_LOGS: (state) => state.loadingParams.display_Logs,
    HEIGHT_OFFSET: (state) =>
      state.heightOffset !== null &&
      state.heightOffset !== undefined &&
      state.heightOffset !== ''
        ? parseInt(state.heightOffset)
        : 0,
    DEFAULT_LANG: (state) => state.loadingParams.default_Lang,
    RESET_PASSWORD: (state) => state.resetPassword,
    IS_AUTHENTICATED: (state) => state.authenticated,
    USERNAME: (state) => state.username,
    TIMEOUT: (state) => state.timeout,
    USER_HAS_ALL_DOMAIN: (state) => state.userRights.some((ur) => ur.domainId === 1),
    USER_HAS_BANKS_DOMAIN: (state) => state.userRights.some((ur) => ur.domainId === 2),
    USER_HAS_BRANCHES_DOMAIN: (state) => state.userRights.some((ur) => ur.domainId === 1),
    CAN_SEE_CARDS: (state) => state.userRights.some((ur) => ur.rightId === 1),
    CAN_MANAGE_CARDS: (state) => state.userRights.some((ur) => ur.rightId === 2),
    CAN_SEE_USERS: (state) => state.userRights.some((ur) => ur.rightId === 4 || ur.rightId === 3),
    CAN_SEE_ROLES: (state) => state.userRights.some((ur) => ur.rightId === 5 || ur.rightId === 3),
    RIGHTS_ON_BANK_LEVEL: (state) => state.userRights.some((ur) => ur.domainId === 2),
    CAN_SEE_BANKS: (state) => state.userRights.some((ur) => ur.rightId === 3),
    CAN_ADD_BANKS: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId === 1
      );
    },
    CAN_EDIT_BANKS: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 2
      );
    },
    CAN_DELETE_BANKS: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId === 1
      );
    },
    CARDS_RIGHTS_ON_BANK_LEVEL: (state) => {
      var ur_c = state.userRights.filter((ur) => ur.rightId === 1);
      var ur_c_b = ur_c.filter((ur) => ur.domainId >= 2);

      return ur_c.length === ur_c_b.length;
    },
    CARDS_RIGHTS_ON_BRANCH_LEVEL: (state) => {
      return (
        state.userRights.filter((ur) => ur.rightId === 1 && ur.domainId === 3)
          .length === 1
      );
    },
    RIGHTS_ON_BRANCH_LEVEL: (state) => {
      return state.userRights.some((ur) => ur.domainId === 3);
    },
    CAN_SEE_BRANCHES: (state) => {
      return state.userRights.some((ur) => ur.rightId === 3);
    },
    CAN_ADD_BRANCHES: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 2
      );
    },
    CAN_EDIT_BRANCHES: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    CAN_DELETE_BRANCHES: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 2
      );
    },
    CAN_PIN_UNPIN_BRANCHES: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    CAN_SEE_DEVICES: (state) => {
      return state.userRights.some((ur) => ur.rightId === 3);
    },
    CAN_ADD_DEVICES: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    CAN_EDIT_DEVICES: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    CAN_DELETE_DEVICES: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    CAN_SEE_PRODUCTS: (state) => state.userRights.some((ur) => ur.rightId === 3),
    CAN_ADD_PRODUCTS: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    CAN_EDIT_PRODUCTS: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    CAN_DELETE_PRODUCTS: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    CAN_MANAGE_ROLES: (state) => state.userRights.some((ur) => ur.rightId === 3),
    CAN_SEE_LOGS: (state) => state.userRights.some((ur) => ur.rightId === 11 ),
    CAN_SEE_DEVICES_MONITORING: (state) => state.userRights.some((ur) => ur.rightId === 10),
    CAN_SEE_SERVICE_STATUSES: (state) => state.userRights.some((ur) => ur.rightId === 3 && ur.rightAndDomainId === 7 && ur.domainId === 1),
    CAN_SEE_KIOSKS: (state) => state.loadingParams.enable_Kiosks,
    ENABLE_CANCEL_CARDS: (state) => state.loadingParams.enable_To_Cancel,
    ENABLE_TO_FINAL: (state) => state.loadingParams.enable_To_Final,
    ENABLE_TO_CENTRAL: (state) => state.loadingParams.enable_To_Central,
    ENABLE_TO_CENTRAL_PRINT: (state) => state.loadingParams.enable_To_Central_Print,
    DEFAULT_DARK_THEME: (state) => state.loadingParams.default_Dark_Theme,
    UPDATE_FREQUENCY: (state) => (state.loadingParams.update_Frequency || '').split('#').map((val) => parseInt(val, 10)).sort((a, b) => a - b),
    COUNT_ROWS: (state) => (state.loadingParams.count_Rows || '').split('#').map((val) => parseInt(val, 10)).sort((a, b) => a - b),

    USER_DATA: (state) => state.user,
    IS_DEBUG: (state) => state.isDebug,
    CAN_SEND_TO_CENTRAL: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 12 && ur.domainId <= 2
      );
    },
    CAN_MANAGE_KEY_TPK: (state) => {
      return state.userRights.some(
        (ur) => ur.rightId === 5 && ur.domainId <= 3
      );
    },
    SERVICES: (state) => state.services,
  },
  mutations: {
    set_token(state, token) {
      state.token = token;
    },
    remove_token(state) {
      state.token = '';
    },
    auth_request(state) {
      state.status = 'loading';
    },
    auth_success(state, { rights, name, timeout, usData }) {
      state.status = 'success';
      state.userRights = rights;
      state.username = name;
      state.user = usData;
      state.timeout =
        timeout === null || timeout === undefined || timeout === 0
          ? state.deftimeout
          : timeout;
    },
    reauth_success(state, { rights, name, timeout }) {
      state.status = 'success';
      state.userRights = rights;
      state.username = name;
      state.timeout =
        timeout === null || timeout === undefined || timeout === 0
          ? state.deftimeout
          : timeout;
    },
    auth_error(state) {
      state.status = 'error';
      state.token = '';
    },
    logout(state) {
      state.status = '';
      state.token = '';
      state.username = '';
      state.userRights = [];
    },
    setResetPassword(state) {
      state.resetPassword = true;
    },
    setNotResetPassword(state) {
      state.resetPassword = false;
    },
    setAuthenticated(state) {
      state.authenticated = true;
    },
    setUnathenticated(state) {
      state.authenticated = false;
    },
    setAdminPassword(state, password) {
      state.adminPassword = password;
    },
    setAdminSettings(state, settings) {
      state.adminSettings = settings;
    },
    setLoadingParams(state, settings) {
      state.loadingParams = settings;
    },
    setUsername(state, val) {
      state.username = val;
    },
    resetUsername(state) {
      state.username = '';
    },
    setCurrentUserRights(state, val) {
      state.userRights = val;
    },
    clearCurrentUserRights(state) {
      state.userRights = [];
    },
    setAuthToken(state, val) {
      state.authToken = val;
    },
    setTotalCount(state, val) {
      state.totalCount = val;
    },
    resetAuthToken(state) {
      state.authToken = '';
    },
    setBanks(state, val) {
      state.banks = val;
    },
    resetBanks(state) {
      state.banks = [];
    },
  },
});
